import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, CrosshairMode } from 'lightweight-charts';

// ── Constants ─────────────────────────────────────────────────────────────────
const BARS_EACH = 400; // bars in each direction from the center quarter

// ── Helpers ───────────────────────────────────────────────────────────────────
function dateToUnix(isoStr) {
  return Math.floor(new Date(isoStr).getTime() / 1000);
}

/**
 * Slice candles to BARS_EACH in each direction from centerIso date.
 * Returns { sliced, centerIdx } where centerIdx is the index of the
 * center candle inside the sliced array.
 */
function sliceAroundCenter(candles, centerIso, barsEach) {
  if (!candles.length) return { sliced: [], centerIdx: 0 };
  const ts = dateToUnix(centerIso);
  let ci = candles.findIndex(c => c.time >= ts);
  if (ci === -1) ci = candles.length - 1;
  const start = Math.max(0, ci - barsEach);
  const end   = Math.min(candles.length, ci + barsEach + 1);
  return { sliced: candles.slice(start, end), centerIdx: ci - start };
}

/** Pick the best ticker from Yahoo search results — prefer US exchanges. */
function bestTicker(candidates) {
  const US = new Set(['NYQ', 'NMS', 'NGM', 'ASE', 'PCX', 'NYB', 'BATS', 'NYSE ARCA']);
  return candidates.find(c => c.isUS) || candidates[0] || null;
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const mkBtn = (active) => ({
  padding: '3px 10px', fontSize: 10, fontWeight: 600,
  border: '1px solid #21262d', borderRadius: 4,
  background: active ? '#58a6ff' : 'transparent',
  color:      active ? '#0d1117' : '#8b949e',
  cursor: 'pointer', fontFamily: 'monospace',
});

const inputStyle = {
  background: '#0d1117', border: '1px solid #30363d', color: '#e6edf3',
  padding: '4px 8px', fontSize: 11, borderRadius: 4,
  fontFamily: 'monospace', outline: 'none',
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function CandlestickChart({ companyName, centerDate, onTickerResolved }) {
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const candleRef    = useRef(null);
  const volRef       = useRef(null);

  const [interval, setIntervalMode] = useState('1d');

  // Resolved ticker symbol
  const [ticker,       setTicker]       = useState(null);
  const [tickerInput,  setTickerInput]  = useState('');
  const [editingTicker, setEditingTicker] = useState(false);
  const [candidates,   setCandidates]   = useState([]);

  // Price data
  const [priceData,   setPriceData]   = useState(null);  // { candles, currency }
  const [dataSource,  setDataSource]  = useState('');

  // UI state
  const [loading,    setLoading]    = useState(false);
  const [searchMsg,  setSearchMsg]  = useState('');
  const [error,      setError]      = useState(null);
  const [ohlcv,      setOhlcv]      = useState(null);   // crosshair readout

  // ── 1. Init lightweight-charts (once on mount) ──────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      layout: {
        background: { color: '#0d1117' },
        textColor:  '#8b949e',
      },
      grid: {
        vertLines: { color: '#21262d' },
        horzLines: { color: '#21262d' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: '#21262d' },
      timeScale: {
        borderColor:    '#21262d',
        timeVisible:    true,
        secondsVisible: false,
      },
      handleScroll: true,
      handleScale:  true,
    });

    // Candlestick series
    const candleSeries = chart.addCandlestickSeries({
      upColor:        '#00D4AA',
      downColor:      '#FF6B6B',
      borderUpColor:  '#00D4AA',
      borderDownColor:'#FF6B6B',
      wickUpColor:    '#00D4AA',
      wickDownColor:  '#FF6B6B',
    });

    // Volume histogram — occupies the bottom ~13% of the chart
    const volSeries = chart.addHistogramSeries({
      color:       '#26a69a',
      priceFormat: { type: 'volume' },
      priceScaleId: 'vol',
    });
    chart.priceScale('vol').applyOptions({
      scaleMargins: { top: 0.87, bottom: 0 },
    });

    // Crosshair → update OHLCV readout
    chart.subscribeCrosshairMove(param => {
      if (!param.time || !param.seriesData?.size) { setOhlcv(null); return; }
      const d = param.seriesData.get(candleSeries);
      if (d) setOhlcv(d);
    });

    chartRef.current   = chart;
    candleRef.current  = candleSeries;
    volRef.current     = volSeries;

    // Resize observer — keeps chart filling its container
    const ro = new ResizeObserver(([entry]) => {
      chart.applyOptions({
        width:  entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    ro.observe(containerRef.current);

    return () => { ro.disconnect(); chart.remove(); };
  }, []);

  // ── 2. Resolve ticker from company name ────────────────────────────────────
  useEffect(() => {
    if (!companyName) {
      setTicker(null); setCandidates([]); setPriceData(null);
      setError(null); setSearchMsg('');
      onTickerResolved?.(null);
      return;
    }
    setTicker(null);
    setError(null);
    setCandidates([]);
    setEditingTicker(false);  // close any open form from a previous stock
    setSearchMsg(`Searching ticker for "${companyName}"…`);

    fetch(`/api/search?q=${encodeURIComponent(companyName)}`)
      .then(r => r.json())
      .then(results => {
        setSearchMsg('');
        if (!Array.isArray(results) || !results.length) {
          // Don't auto-open the form — just show the error overlay with a manual button
          setError(`No ticker found for "${companyName}".`);
          onTickerResolved?.(null);
          return;
        }
        setCandidates(results);
        const best = bestTicker(results);
        // Auto-select and auto-fetch — no user interaction required
        if (best) { setTicker(best.symbol); setTickerInput(best.symbol); onTickerResolved?.(best.symbol); }
      })
      .catch(e => { setSearchMsg(''); setError(e.message); });
  }, [companyName]);

  // ── 3. Fetch OHLCV when ticker or interval changes ─────────────────────────
  useEffect(() => {
    if (!ticker) { setPriceData(null); return; }
    setLoading(true);
    setError(null);

    fetch(`/api/price/${encodeURIComponent(ticker)}?interval=${interval}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); setLoading(false); return; }
        setPriceData({ candles: data.candles, currency: data.currency });
        setDataSource(data.source);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [ticker, interval]);

  // ── 4. Render candles whenever priceData or centerDate changes ─────────────
  useEffect(() => {
    if (!priceData?.candles || !centerDate || !candleRef.current) return;

    const { sliced, centerIdx } = sliceAroundCenter(priceData.candles, centerDate, BARS_EACH);
    if (!sliced.length) return;

    // Set candlestick data
    candleRef.current.setData(
      sliced.map(c => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close }))
    );

    // Set volume data (color matches candle direction)
    volRef.current.setData(
      sliced.map(c => ({
        time:  c.time,
        value: c.volume,
        color: c.close >= c.open ? 'rgba(0,212,170,0.3)' : 'rgba(255,107,107,0.3)',
      }))
    );

    // Arrow marker at the center quarter date
    const centerCandle = sliced[centerIdx];
    if (centerCandle) {
      candleRef.current.setMarkers([{
        time:     centerCandle.time,
        position: 'aboveBar',
        color:    '#FFEAA7',
        shape:    'arrowDown',
        text:     '13F',
      }]);
    }

    // Initial visible range: 200 bars each side of center (user can zoom out to 400)
    chartRef.current.timeScale().setVisibleLogicalRange({
      from: centerIdx - 200,
      to:   centerIdx + 200,
    });
  }, [priceData, centerDate]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleTickerSubmit = (e) => {
    e.preventDefault();
    const t = tickerInput.trim().toUpperCase();
    if (t) { setTicker(t); setEditingTicker(false); }
  };

  /** Re-center the chart on the 13F quarter date without changing zoom level. */
  const resetCenter = useCallback(() => {
    if (!priceData?.candles || !centerDate || !chartRef.current) return;
    const { centerIdx } = sliceAroundCenter(priceData.candles, centerDate, BARS_EACH);
    const range = chartRef.current.timeScale().getVisibleLogicalRange();
    const half  = range ? (range.to - range.from) / 2 : 200;
    chartRef.current.timeScale().setVisibleLogicalRange({
      from: centerIdx - half,
      to:   centerIdx + half,
    });
  }, [priceData, centerDate]);

  /** Zoom in or out by a factor (0.5 = zoom in, 2 = zoom out). */
  const zoom = (factor) => {
    const range = chartRef.current?.timeScale().getVisibleLogicalRange();
    if (!range) return;
    const mid  = (range.from + range.to) / 2;
    const half = ((range.to - range.from) / 2) * factor;
    chartRef.current.timeScale().setVisibleLogicalRange({ from: mid - half, to: mid + half });
  };

  // ── Overlay logic ──────────────────────────────────────────────────────────
  let overlayText  = null;
  let overlayError = false;
  if (!companyName) {
    overlayText = 'Click a holding to view its price chart';
  } else if (error) {
    overlayText  = error;
    overlayError = true;
  } else if (!ticker && !searchMsg && !loading) {
    overlayText  = `No ticker found for "${companyName}". Enter one manually.`;
    overlayError = true;
  }
  const showOverlay = !loading && overlayText !== null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 8, padding: 16, marginBottom: 20 }}>

      {/* ── Header row ────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>

        {/* Left: title + resolved ticker */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: '#8b949e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Price Chart
          </span>
          {ticker && (
            <>
              <span style={{ fontSize: 12, color: '#58a6ff', fontWeight: 700 }}>{ticker}</span>
              <span style={{ fontSize: 9, color: '#8b949e', opacity: 0.7 }}>[{dataSource}]</span>
              <button
                style={{ ...mkBtn(false), padding: '2px 6px', fontSize: 9 }}
                onClick={() => setEditingTicker(v => !v)}
              >
                change
              </button>
            </>
          )}
          {searchMsg && (
            <span style={{ fontSize: 10, color: '#8b949e', fontStyle: 'italic' }}>{searchMsg}</span>
          )}
        </div>

        {/* Right: OHLCV readout + controls */}
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
          {ohlcv && (
            <span style={{ fontSize: 10, fontFamily: 'monospace', marginRight: 6, color: '#8b949e' }}>
              O&nbsp;<b style={{ color: '#e6edf3' }}>{ohlcv.open?.toFixed(2)}</b>
              &nbsp;H&nbsp;<b style={{ color: '#00D4AA' }}>{ohlcv.high?.toFixed(2)}</b>
              &nbsp;L&nbsp;<b style={{ color: '#FF6B6B' }}>{ohlcv.low?.toFixed(2)}</b>
              &nbsp;C&nbsp;<b style={{ color: '#58a6ff' }}>{ohlcv.close?.toFixed(2)}</b>
            </span>
          )}
          <button style={mkBtn(interval === '1d')}  onClick={() => setIntervalMode('1d')}>Daily</button>
          <button style={mkBtn(interval === '1wk')} onClick={() => setIntervalMode('1wk')}>Weekly</button>
          <button style={mkBtn(false)} onClick={() => zoom(0.5)} title="Zoom in">+</button>
          <button style={mkBtn(false)} onClick={() => zoom(2.0)} title="Zoom out">−</button>
          {priceData && (
            <button style={mkBtn(false)} onClick={resetCenter} title="Re-center on 13F quarter">⊕</button>
          )}
        </div>
      </div>

      {/* ── Ticker override / candidate picker ────────────────────────────── */}
      {editingTicker && (
        <div style={{ marginBottom: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <form onSubmit={handleTickerSubmit} style={{ display: 'flex', gap: 6 }}>
            <input
              value={tickerInput}
              onChange={e => setTickerInput(e.target.value)}
              placeholder="e.g. AAPL"
              style={{ ...inputStyle, width: 130 }}
              autoFocus
            />
            <button type="submit" style={mkBtn(true)}>Go</button>
          </form>
          {candidates.slice(0, 6).map(c => (
            <button
              key={c.symbol}
              style={{ ...mkBtn(c.symbol === ticker), fontSize: 9, padding: '2px 8px' }}
              onClick={() => { setTicker(c.symbol); setTickerInput(c.symbol); setEditingTicker(false); }}
              title={c.name}
            >
              {c.symbol}&nbsp;<span style={{ opacity: 0.6 }}>({c.exchange})</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Chart area ────────────────────────────────────────────────────── */}
      <div style={{ position: 'relative', height: 420 }}>

        {/* lightweight-charts canvas target — always in the DOM */}
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

        {/* Loading overlay */}
        {loading && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            background: 'rgba(13,17,23,0.88)',
          }}>
            <div style={{
              width: 20, height: 20,
              border: '2px solid #21262d', borderTop: '2px solid #58a6ff',
              borderRadius: '50%', animation: 'spin 1s linear infinite',
            }} />
            <span style={{ color: '#8b949e', fontSize: 11 }}>Fetching {ticker}…</span>
          </div>
        )}

        {/* Empty / error overlay */}
        {showOverlay && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
            background: '#0d1117',
          }}>
            <div style={{ fontSize: 28, opacity: 0.2 }}>
              {overlayError ? '⚠' : '🕯'}
            </div>
            <div style={{
              color: overlayError ? '#FF6B6B' : '#8b949e',
              fontSize: 11, textAlign: 'center', maxWidth: 300,
            }}>
              {overlayText}
            </div>
            {overlayError && (
              <button style={mkBtn(true)} onClick={() => setEditingTicker(true)}>
                Enter ticker manually
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
